<template>
    <Popover
        v-slot="slotProps"
        class="relative"
        as="span"
        :class="popoverWrapperClasses">
        <div
            class="leading-3 inline"
            @mouseenter="handlePopoverMouseEnter(slotProps, $event)"
            @mouseleave="handlePopoverMouseLeave(slotProps, $event)">
            <PopoverButton
                ref="popoverTrigger"
                as="span"
                class="text-sm cursor-pointer">
                <slot name="trigger" />
            </PopoverButton>

            <Transition
                v-if="!disabled"
                enter-active-class="transition ease-out duration-200"
                enter-from-class="opacity-0 translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-1">
                <PopoverPanel
                    class="absolute z-[60] flex w-screen max-w-max transition-opacity p-2 -mx-2"
                    :class="basePositioning"
                    :static="showAlways"
                    :style="offset">
                    <div
                        ref="panel"
                        :class="popoverClasses + ' ' + popoverStyles">
                        <slot />
                    </div>
                </PopoverPanel>
            </Transition>
        </div>
    </Popover>
</template>

<script setup>
const popoverTrigger = ref(null)

const props = defineProps({
    size: {
        type: String,
        default: 'md',
        description: 'large: lg || medium: md || small: sm'
    },
    position: {
        type: String,
        default: 'center',
        description: 'center || left || right || top || bottom || top-right || top-left || bottom-right || bottom-left'
    },
    trigger: {
        type: String,
        default: 'hover',
        description: 'hover || click'
    },
    showAlways: {
        type: Boolean,
        default: false,
        description: 'show popover always'
    },
    disabled: {
        type: Boolean,
        default: false,
        description: 'disable popover'
    },
    popoverColor: {
        type: String,
        default: 'default',
        description: 'color for popover (default || primary)'
    },
    popoverWrapperClasses: {
        type: String,
        default: '',
        description: 'additional classes for popover wrapper'
    },
    popoverClasses: {
        type: String,
        default: 'flex-auto rounded-xl bg-white px-6 py-4 text-sm bg-white dark:bg-gray-800 text-sm leading-6 shadow ring-1 dark:ring-gray-300 ring-gray-900/5',
        description: 'additional classes for popover'
    },
    noMaxWidth: {
        type: Boolean,
        default: false,
        description: 'remove max-width from popover'
    },
    withArrow: {
        type: Boolean,
        default: false,
        description: 'show arrow of popover'
    }
})

const panel = ref(null)

const panelBoundingRect = useElementBounding(panel)

const {width: windowWidth} = useWindowSize()

const yPosition = computed(() => {
    if (props.position === 'top' || props.position === 'top-left' || props.position === 'top-right') return 'top'
    if (props.position === 'left' || props.position === 'right') return 'center'
    return 'bottom'
})

const xPosition = computed(() => {
    if (props.position === 'left' || props.position === 'top-left' || props.position === 'bottom-left') return 'left'
    if (props.position === 'right' || props.position === 'top-right' || props.position === 'bottom-right') return 'right'
    return 'center'
})

const offset = computed(() => {
    if (!panelBoundingRect) return ''

    const PADDING = 15
    // popover default positions
    const posX = panelBoundingRect.x.value
    const rightX = posX + panelBoundingRect.width.value

    const posY = panelBoundingRect.y.value
    const bottomY = posY + panelBoundingRect.height.value

    const leftOffset = (posX < 0) ? Math.floor(posX) - PADDING : 0
    const rightOffset = (rightX > windowWidth.value) ? Math.floor(rightX - windowWidth.value) + PADDING : 0

    const topOffset = (posY < 0) ? Math.floor(posY) - PADDING : 0
    const bottomOffset = (bottomY > window.innerHeight) ? Math.floor(bottomY - window.innerHeight) + PADDING : 0

    let transform = ''

    if (yPosition.value === 'top') transform += `translateY(calc(-100% - ${topOffset}px - ${bottomOffset}px)) `
    if (yPosition.value === 'center') transform += `translateY(calc(-50% - ${topOffset}px - ${bottomOffset}px)) `
    if (yPosition.value === 'bottom') transform += `translateY(calc(${topOffset}px - ${bottomOffset}px)) `

    if (xPosition.value === 'center') transform += `translateX(calc(-50% - ${leftOffset}px - ${rightOffset}px))`
    if (xPosition.value === 'left') transform += `translateX(calc(-100% - ${leftOffset}px - ${rightOffset}px))`
    if (xPosition.value === 'right') transform += `translateX(calc(${leftOffset}px - ${rightOffset}px))`

    return transform ? ('transform: ' + transform + ';') : ''
})

const closeTimeout = ref(null)

function handlePopoverMouseEnter({open}, event) {
    if (props.disabled) return
    clearTimeout(closeTimeout.value)
    if (open || props.trigger === 'click' || event.sourceCapabilities?.firesTouchEvents) return
    if (popoverTrigger.value) {
        popoverTrigger.value.$el.click()
    }
}

function handlePopoverMouseLeave({open, close}, event) {
    if (props.disabled) return
    if (!open || props.trigger === 'click' || event.sourceCapabilities?.firesTouchEvents) return

    closeTimeout.value = setTimeout(close, 250)
}

const popoverStyles = computed(() => {
    let styles = ''

    if (props.size === 'sm') {
        styles = 'max-w-sm'
    } else if (props.size === 'md') {
        styles = 'max-w-md'
    } else if (props.size === 'xl') {
        styles = 'max-w-xl'
    } else {
        styles = 'max-w-lg'
    }

    styles += ' '

    if (props.popoverColor === 'primary') {
        styles += 'bg-primary-100 dark:bg-primary-800 text-primary-500 dark:text-primary-200'
    } else {
        styles += 'bg-white dark:bg-gray-800'
    }

    return styles
})

// position needs to be relative to different sides of the trigger, depending on the position prop
const basePositioning = computed(() => {
    let classes = ''
    if (yPosition.value === 'top') classes += 'top-0 '
    if (yPosition.value === 'center') classes += 'top-1/2 '

    if (yPosition.value === 'center') {
        if (xPosition.value === 'left') classes += 'left-0 '
        if (xPosition.value === 'center') classes += 'left-1/2 '
        if (xPosition.value === 'right') classes += 'left-full '
    } else {
        if (xPosition.value === 'left') classes += 'left-full'
        if (xPosition.value === 'center') classes += 'left-1/2 '
        if (xPosition.value === 'right') classes += 'left-0'
    }

    return classes
})
</script>

<style scoped>

</style>